<script lang="ts" setup>
interface Props {
  item: MaterialItemApp;
  showFavoriteButton?: boolean;
  itemType: SegmentKey;
}

interface Emits {
  (event: 'open-wholesaler-modal'| 'open-location-modal', item: MaterialItemApp): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const { n, t } = useEkI18n();
const { openToast } = useUserMessages();
const CatalogAPI = useCatalogAPI();
const wholesalerItemsStore = useWholesalerItemsStore();
const materialFavoriteStore = useMaterialFavoriteListsConnectedToFitterStore();
const searchAllItemsStore =useSearchAllItemsStore();
const { _allItemsRef } = storeToRefs(searchAllItemsStore);
const { _wholesalerItems, _onlyOwnItems } = storeToRefs(wholesalerItemsStore);
const { favoriteListItems } = storeToRefs(materialFavoriteStore);

const itemIsFavorite = ref(props.item.IsFavorite);
const popoverOpen = ref<boolean>(false);
const id = useId();

enum ActionType {
  ADD,
  REMOVE
}

async function toggleIsAddedToFavoriteList() {
  let listId = materialFavoriteStore.favoriteLists.find(list => list.IsPersonalList)?.ListId;
  const recordIds = props.item.RecordId ? [Number.parseInt(props.item.RecordId)] : [];

  // If listId doesn't exist, create a new list
  if (!listId) {
    listId = await createPersonalFavoriteList();

    // If failed then return
    if (!listId) {
      openToast(t('alert.errorOccurred'), ToastType.ERROR);

      return;
    }
  }

  const action = itemIsFavorite.value ? ActionType.REMOVE : ActionType.ADD;
  await updateFavorites(action, listId, recordIds);
}

async function createPersonalFavoriteList() {
  const response = await CatalogAPI.CreatePersonalMaterialFavoriteList({ ListName: 'Personlige favoritter' });

  if (response?.HasErrors === false) {
    await materialFavoriteStore.setMaterialFavoriteListsConnectedToFitter();

    return response.Data.ListId;
  }
}

async function updateFavorites(action: ActionType, listId: number, recordIds: number[]) {
  let response;

  if (action === ActionType.ADD) {
    response = await CatalogAPI.addMaterialsToFavoriteList({ ListID: listId, RecordIds: recordIds });
    
  } else if (action === ActionType.REMOVE) {
    response = await CatalogAPI.removeMaterialsFromFavoriteList({ ListID: listId, RecordIds: recordIds });
  }

  if (response?.HasErrors === false) {
    updateIsFavoriteInStores();

    itemIsFavorite.value = !itemIsFavorite.value;

    openToast(
      action === ActionType.ADD ? t('toast.addedToFavoriteList') : t('toast.removeFromFavoriteList'),
      ToastType.SUCCESS
    );
  }
}
  
function updateIsFavoriteInStores() {
  function findItem(StoreList: MaterialItemApp[] | undefined, recordId: string) {
    if (StoreList === undefined) return;

    return StoreList?.find(i => i.RecordId === recordId);
  }

  function toggleFavoriteStatus(item: MaterialItemApp | undefined) {
    if (item !== undefined) item.IsFavorite = !itemIsFavorite.value;
  }

  switch (props.itemType) {
    case 'allItems': {
      const allItems = findItem(_allItemsRef.value, props.item.RecordId);
      const wholesalerItem = findItem(_wholesalerItems.value?.SearchItems, props.item.RecordId);      
      
      toggleFavoriteStatus(wholesalerItem);
      toggleFavoriteStatus(allItems);

      break;
    }
    case 'wholesalerCatalog': {
      const allItems = findItem(_allItemsRef.value, props.item.RecordId);
      const wholesalerItem = findItem(_wholesalerItems.value?.SearchItems, props.item.RecordId);      

      toggleFavoriteStatus(wholesalerItem);
      toggleFavoriteStatus(allItems);
      
      break;
    }
    case 'favorites': {
      const wholesalerItemFromFavorite = findItem(_wholesalerItems.value?.SearchItems, props.item.RecordId);
      const ownItemFromFavorite = findItem(_onlyOwnItems.value?.SearchItems, props.item.RecordId);
      const favoriteItemIndex = favoriteListItems.value?.Items.findIndex(item => item.RecordId === props.item.RecordId);
      const allItems = findItem(_allItemsRef.value, props.item.RecordId);  
      const favoriteItems = favoriteListItems.value?.Items.find(item => item.RecordId === props.item.RecordId);

      toggleFavoriteStatus(wholesalerItemFromFavorite);
      toggleFavoriteStatus(ownItemFromFavorite);
      toggleFavoriteStatus(allItems);
      
      if (favoriteItemIndex !== undefined && favoriteItemIndex !== -1 && materialFavoriteStore.isPersonal) {
        favoriteListItems.value?.Items.splice(favoriteItemIndex, 1);
      } else {
        toggleFavoriteStatus(wholesalerItemFromFavorite);
        toggleFavoriteStatus(ownItemFromFavorite);
        toggleFavoriteStatus(allItems); 
        toggleFavoriteStatus(favoriteItems); 
      }

      break;
    }
    case 'ownItems': {
      const ownItem = findItem(_onlyOwnItems.value?.SearchItems, props.item.RecordId);
      const allItems = findItem(_allItemsRef.value, props.item.RecordId);    

      toggleFavoriteStatus(ownItem);
      toggleFavoriteStatus(allItems);

      break;
    }
  }
}

function shouldShowFavoriteButton() {
  if (props.showFavoriteButton === true) {
    return props.showFavoriteButton;
  }

  switch (props.itemType) {
    case 'allItems':
      //can't add standard lines or location items to favorites
      if (props.item.Location === null && !(props.item.CatalogId === 1 && !props.item.WholesalerName)) {
        return true;
      }

      break;
    case 'wholesalerCatalog':
      return true;
    case 'favorites':
      if (props.item.IsFavorite === true) {
        return true;
      }

      break;
    case 'ownItems':
      return true;
    default:
      return false;
  }
}

function openModal() {
  if (props.item.Location === null) {
    emit('open-wholesaler-modal', props.item);
  } else {
    emit('open-location-modal', props.item);
  }
}

const subtitle = computed(() => {
  let wholesalerName = props.item.WholesalerName ?? '';
  const locationName = props.item.Location?.Name ?? '';

  if (props.item.CatalogId === 1 && !props.item.WholesalerName) {
    wholesalerName = t('navbar.standardLines', 1);
  }

  if (wholesalerName && locationName) {
    return `${wholesalerName} | ${locationName}`;
  } else {
    return wholesalerName || locationName;
  }
});

</script>

<template>
  <IonItem
    lines="full"
    color="card-background"
    @click="openModal"
  >
    <div
      slot="start"
      class="thumbnail"
    >
      <div
        v-if="shouldShowFavoriteButton()"
        class="fav-icon-container"
        @click.stop="toggleIsAddedToFavoriteList"
      >
        <FontAwesomeIcon
          class="fav-icon"
          :icon="[item.IsFavorite ? 'fas' : 'fal', 'heart']"
        />
      </div>
      <IonThumbnail class="image">
        <img
          v-if="item.ImageUrl"
          :src="item.ImageUrl"
        >
        <FontAwesomeIcon
          v-else
          :icon="['fas', 'camera-slash']"
        />
      </IonThumbnail>
    </div>
    <IonGrid class="ion-no-padding grid-container">
      <div class="title">
        <div class="title-wrapper">
          {{ item.Name }}
          <IonNote v-if="item.Unit?.ShortName">
            {{ item.Unit.ShortName }}
          </IonNote>
        </div>
        <div
          v-if="item.ItemAmount !== undefined && item.ItemAmount !== null"
          :id="id"
          class="cart-line"
          @click.stop="popoverOpen = !popoverOpen"
        >
          <FontAwesomeIcon :icon="['fas', 'pallet-boxes']" />
          <span>{{ item.ItemAmount }}</span>
        </div>
      </div>
      <IonRow
        v-if="subtitle"
        class="subtitle"
      >
        {{ subtitle }}
      </IonRow>
      <IonRow class="ion-justify-content-between medium-padding-top">
        <IonCol
          class="ion-no-padding details"
          size="auto"
        >
          <IonRow class="detail">
            <div class="detail-content">
              <span><strong>EAN</strong></span>
            </div>
            <span>{{ item.Ean ?? t('labels.notAvailable') }}</span>
          </IonRow>
          <IonRow class="detail">
            <div class="detail-content">
              <FontAwesomeIcon :icon="['fal', 'rectangle-barcode']" />
            </div>
            <span>{{ item.Barcode ?? t('labels.notAvailable') }}</span>
          </IonRow>
        </IonCol>
        <IonCol
          v-if="item.SalesPrice"
          size="auto"
          class="ion-no-padding price"
        >
          <IonRow>
            <span class="unit">{{ t('materials.label.salesPrice') + ' ' + t('currency.DKK') }}</span>
          </IonRow>
          <IonRow>
            <span><strong>{{ n(item.SalesPrice, 'currency') ?? t('labels.notAvailable') }}</strong></span>
          </IonRow>
        </IonCol>
        <IonCol
          v-if="item.Price !== null"
          size="auto"
          class="ion-no-padding price"
        >
          <IonRow>
            <span class="unit">{{ t('currency.DKK') + '/' + item.Unit?.ShortName?.toLowerCase() }}</span>
          </IonRow>
          <IonRow>
            <span><strong>{{ n(item.Price, 'currency') ?? t('labels.notAvailable') }}</strong></span>
          </IonRow>
        </IonCol>
      </IonRow>
    </IonGrid>
  </IonItem>
  <IonPopover
    v-if="item.ItemAmount"
    :show-backdrop="false"
    :trigger="id"
  >
    <IonContent class="ion-padding">
      {{ t('materials.popover.itemAmount', item.ItemAmount) }}
    </IonContent>
  </IonPopover>
</template>

<style scoped>
.details {
  display: flex;
  font-size: smaller;
  flex-direction: column;
}

.detail {
  display: flex;
  justify-items: start;
  align-items: center;
  gap: 6px;
  padding: 4px 0px;
  position: relative;
}

.detail-content {
  width: 20px;
  display: flex;
  align-items: center;
  justify-items: start;
  span {
    font-size: 10px;
  }
}

.subtitle {
  font-size: smaller;
  color: var(--ion-color-step-500)
}

.fav-icon-container {
  position: absolute;
  width: 25px;
  border: 1px solid var(--ion-color-step-300);
  height: 25px;
  border-radius: 50%;
  box-shadow: 2px 2px 6px var(--ion-color-step-100);
  background-color: var(--ion-card-background);
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  transform: translate(-50%, -50%);
}

.fav-icon {
  position: relative;
  width: 15px;
  color: var(--ion-color-primary);
}

.cart-line {
  color: var(--ion-color-primary);
  font-weight: bold;
  font-size: smaller;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.thumbnail {
  position: relative;
  margin: 8px 16px 8px 0px;
  .image {
    --border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid var(--ion-color-step-200);
  }
}

.title {
  display: flex;
  justify-content: space-between;
  align-items: start;
}

.price {
  display: flex;
  justify-content: end;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 4px;
  align-items: end;
  justify-items: end;

  .unit {
    font-size: small;
  }
}

.medium-padding-top {
  padding-top: 8px;
}

.grid-container {
  padding: 16px 0px 12px 0px;
}

.title-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-bottom: 0.3rem;
}
</style>
